// apps/codeframe/src/chrome/timeline/useRecordGesture.ts
import { useTimelineStore } from './timeline-store';
import { useCanvasStore } from '../../store/canvas-store';
import { getPlayer } from '../../motion/registry';
import { commitRecordDrag } from './recordDrag';

/**
 * Record-mode node drag. Call from a frame root's pointerdown BEFORE the
 * frame-drag handler; returns true when it claimed the gesture (caller must
 * then skip frame drag/select). frameId = the frame the event landed in,
 * scopedFrameId = the timeline's frame (only that one records).
 * During the drag the node previews via inline translate (same property the
 * player writes — its next tick reconciles, so no cleanup pass needed).
 */
export function handleRecordPointerDown(e: PointerEvent, frameId: string, scopedFrameId: string | undefined): boolean {
  if (!useTimelineStore.getState().recording) return false;
  if (!scopedFrameId || frameId !== scopedFrameId) return false;
  const target = e.target instanceof Element ? e.target.closest('[data-cf-id]') : null;
  if (!target) return false;
  const cfNodeId = target.getAttribute('data-cf-id')!;
  const zoom = useCanvasStore.getState().doc.viewport.zoom;
  const t = getPlayer(frameId)?.time ?? 0;
  const start = { x: e.clientX, y: e.clientY };
  const el = target as HTMLElement;
  const baseTransform = el.style.transform;

  const onMove = (ev: PointerEvent) => {
    const dx = (ev.clientX - start.x) / zoom;
    const dy = (ev.clientY - start.y) / zoom;
    el.style.transform = `${baseTransform} translate(${dx}px, ${dy}px)`;
    showHud(ev.clientX, ev.clientY, dx, dy, t);
  };
  // Shared cleanup: detach ALL listeners, hide the HUD, restore the preview
  // transform. Both the commit path (onUp) and the abort path (onCancel) call
  // this so listeners + HUD can never leak if pointerup never fires.
  const teardown = () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onCancel);
    el.removeEventListener('lostpointercapture', onCancel);
    hideHud();
    el.style.transform = baseTransform;
  };
  const onUp = (ev: PointerEvent) => {
    teardown();
    commitRecordDrag({ frameId, cfNodeId, t, dx: ev.clientX - start.x, dy: ev.clientY - start.y, zoom });
  };
  // Abort (no commit): pointer released off-window, OS stole capture, or a
  // context menu interrupted the gesture. Restore baseTransform, write nothing.
  const onCancel = () => {
    teardown();
  };
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onCancel);
  el.addEventListener('lostpointercapture', onCancel);
  e.preventDefault();
  e.stopPropagation();
  return true;
}

// --- HUD chip (x +24 · y −12 @ 0.48s), one singleton div ---
let hud: HTMLDivElement | null = null;
function showHud(cx: number, cy: number, dx: number, dy: number, t: number): void {
  if (!hud) {
    hud = document.createElement('div');
    hud.className =
      'pointer-events-none fixed z-50 rounded bg-[var(--color-surface)] border border-[var(--color-chip-border)] px-1.5 py-0.5 text-[10px] font-[family-name:var(--font-mono)] shadow-[var(--shadow-1)]';
    document.body.appendChild(hud);
  }
  hud.style.left = `${cx + 12}px`;
  hud.style.top = `${cy + 12}px`;
  hud.textContent = `x ${dx >= 0 ? '+' : ''}${Math.round(dx)} · y ${dy >= 0 ? '+' : ''}${Math.round(dy)} @ ${(t / 1000).toFixed(2)}s`;
}
function hideHud(): void {
  hud?.remove();
  hud = null;
}
